import {
  getAuthorizedGroups,
  isAuthorizedGroups,
} from '@nursio-link/common/authorization';

import type {ComboboxProps} from '@/components/ui/combobox';
import {Combobox} from '@/components/ui/combobox';
import {useGroups} from '@/hooks/useAuth';
import {graphqlClient} from '@/services/graphqlClient';
import type {UserGroup, Warehouse} from '@/services/graphqlHooks';
import {useGetWarehousesQuery} from '@/services/graphqlHooks';

export type WarehouseOption = Warehouse & {
  fallbackText: string;
  value: string;
  label: string;
  keywords: string[];
};

export type WarehouseInputProps = Omit<
  ComboboxProps<WarehouseOption>,
  'items'
> & {
  value: string;
  onChange: (value: string) => void;
  excludedWarehouseId?: string;
};

const WarehouseInput = ({
  value,
  onChange,
  excludedWarehouseId,
  ...props
}: WarehouseInputProps) => {
  const group = useGroups();
  const {data} = useGetWarehousesQuery(graphqlClient, undefined, {
    staleTime: Infinity,
  });

  const warehouses = data?.getWarehouses || [];

  const defaultGroups: UserGroup[] = getAuthorizedGroups(
    'UPDATE_WAREHOUSE_ITEM'
  );

  const warehousesOptions = warehouses
    .filter(
      warehouse =>
        warehouse.id !== excludedWarehouseId &&
        isAuthorizedGroups(
          group || [],
          warehouse.allowedGroups?.length
            ? (warehouse.allowedGroups as UserGroup[])
            : defaultGroups
        )
    )
    .map(warehouse => ({
      keywords: [warehouse.name, warehouse.location, warehouse.id],
      label: warehouse.name,
      value: warehouse.id,
      ...warehouse,
    })) as WarehouseOption[];

  const selectedItem =
    warehousesOptions.find(options => options.value === value) || null;

  return (
    <Combobox
      items={warehousesOptions}
      itemToString={option => option?.label || ''}
      selectedItem={selectedItem}
      onSelectedItemChange={({selectedItem}) => {
        onChange(selectedItem?.value || '');
      }}
      {...props}
    />
  );
};

export default WarehouseInput;
